import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { sessionId } from '../lib/attribution';

/**
 * Frise du dossier visiteur dans /admin/analytics : arrivée, chaque clic (bouton, position, heure),
 * première entrée dans chaque grande section, paliers des vidéos, départ.
 * Envoyé par lots toutes les 20 s à POST /api/analytics/activity (Worker), en beacon quand la page se ferme.
 * Ne touche pas à ReplayRecorder : l'image de l'écran reste enregistrée par rrweb à côté.
 */

const PRIVATE = /^\/(admin|dashboard|formateur|programme)/;
const ENDPOINT = '/api/analytics/activity';

// Identifiants partagés avec VIDEO_LIST dans netlify-proxy/worker.js.
const VIDEOS = [
  [/\/openbro-film-fr\.mp4/, 'film-fr'],
  [/\/openbro-film-en\.mp4/, 'film-en'],
  [/\/ugc\/gaut-smoothie/, 'smoothie'],
  [/\/ugc\/gaut-taco/, 'taco'],
  [/\/ugc\/gaut-burger/, 'burger'],
  [/\/ugc\/gaut-pizza/, 'pizza'],
  [/\/ugc\/gaut-pancake-bites/, 'bites'],
  [/\/ugc\/gaut-pancakes/, 'pancakes'],
];
const MARKS = [25, 50, 75, 100];

function videoId(video) {
  const src = video.currentSrc || video.src || '';
  const hit = VIDEOS.find(([pattern]) => pattern.test(src));
  return hit ? hit[1] : '';
}

function squish(text, max) {
  return String(text || '').replace(/\s+/g, ' ').trim().slice(0, max);
}

function describe(target) {
  const el = target?.closest?.('[data-track], button, a, [role="button"], summary, label, input, select, textarea, video');
  const node = el || target;
  if (!node?.tagName) return { label: '', tag: '', href: '' };
  const tag = node.tagName.toLowerCase();
  const field = tag === 'input' || tag === 'textarea' || tag === 'select';
  // Jamais la valeur d'un champ : seulement ce qui est affiché autour.
  const label = node.getAttribute('data-track')
    || node.getAttribute('aria-label')
    || (field ? node.getAttribute('placeholder') || node.getAttribute('name') : el ? node.innerText : '')
    || node.getAttribute('alt')
    || node.getAttribute('title')
    || '';
  let href = '';
  const raw = el?.getAttribute?.('href');
  if (raw) {
    try {
      const url = new URL(raw, window.location.href);
      href = url.origin === window.location.origin ? `${url.pathname}${url.hash}` : url.hostname;
    } catch {
      href = '';
    }
  }
  return { label: squish(label, 60), tag: el ? tag : '', href };
}

export default function ActivityRecorder() {
  const location = useLocation();
  const hooks = useRef(null);

  useEffect(() => {
    if (PRIVATE.test(window.location.pathname)) return undefined;
    const sid = sessionId();
    const queue = [];
    const push = (item) => {
      if (PRIVATE.test(window.location.pathname)) return;
      queue.push({ at: Date.now(), ...item });
    };
    const send = (useBeacon) => {
      while (queue.length) {
        const items = queue.splice(0, 80);
        const body = JSON.stringify({ sid, page: window.location.pathname, now: Date.now(), items });
        if (useBeacon && navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }))) continue;
        fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body,
          keepalive: body.length < 60000,
        }).catch(() => {});
      }
    };

    push({ k: 'arrive', page: `${window.location.pathname}${window.location.search}`, vw: window.innerWidth, vh: window.innerHeight });

    const onClick = (event) => {
      const { label, tag, href } = describe(event.target);
      const height = document.documentElement.scrollHeight || 1;
      push({
        k: 'click',
        label,
        tag,
        href,
        section: event.target?.closest?.('section[id]')?.id || '',
        x: event.clientX / (window.innerWidth || 1),
        y: (event.clientY + window.scrollY) / height,
      });
    };
    document.addEventListener('click', onClick, true);

    // Une section compte quand elle passe au milieu de l'écran (marche aussi pour les sections plus hautes que l'écran).
    const seenSections = new Set();
    const watched = new WeakSet();
    const sections = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const id = entry.target.id;
        if (!entry.isIntersecting || !id || seenSections.has(id)) return;
        seenSections.add(id);
        const height = document.documentElement.scrollHeight || 1;
        push({ k: 'section', id, top: (entry.target.getBoundingClientRect().top + window.scrollY) / height });
      });
    }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
    const scan = () => {
      document.querySelectorAll('section[id]').forEach((node) => {
        if (watched.has(node)) return;
        watched.add(node);
        sections.observe(node);
      });
    };
    const scans = [0, 1500, 4000].map((delay) => window.setTimeout(scan, delay));

    // Vidéos : point le plus loin atteint en lecture normale (un saut ne compte pas tant que la vidéo ne joue pas ensuite).
    const players = new WeakMap();
    const sentMarks = new Set();
    const onTime = (event) => {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement)) return;
      const id = videoId(video);
      if (!id) return;
      let state = players.get(video);
      if (!state || state.id !== id) {
        state = { id, last: video.currentTime, reach: 0, played: 0 };
        players.set(video, state);
      }
      const delta = video.currentTime - state.last;
      state.last = video.currentTime;
      // Film : compté quand le visiteur a lancé le son. L'aperçu muet tourne tout seul et ne compte pas.
      if (id.startsWith('film') && video.muted) return;
      if (video.paused || !(delta > 0 && delta < 1.5 * (video.playbackRate || 1))) return;
      state.played += delta;
      state.reach = Math.max(state.reach, video.currentTime);
      const duration = video.duration;
      if (!duration || !Number.isFinite(duration)) return;
      const pct = (state.reach / duration) * 100;
      const reached = state.played >= 1 ? [0] : [];
      MARKS.forEach((mark) => { if (pct >= (mark === 100 ? 95 : mark)) reached.push(mark); });
      reached.forEach((mark) => {
        const key = `${id}:${mark}`;
        if (sentMarks.has(key)) return;
        sentMarks.add(key);
        push({ k: 'video', id, mark });
      });
    };
    document.addEventListener('timeupdate', onTime, true);

    const timer = window.setInterval(() => send(false), 20000);
    const onHidden = () => { if (document.visibilityState === 'hidden') send(true); };
    const onLeave = () => {
      push({ k: 'leave' });
      send(true);
    };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onLeave);
    hooks.current = { push, scan };
    return () => {
      hooks.current = null;
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('timeupdate', onTime, true);
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onLeave);
      window.clearInterval(timer);
      scans.forEach((id) => window.clearTimeout(id));
      sections.disconnect();
      send(true);
    };
  }, []);

  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return undefined;
    }
    hooks.current?.push({ k: 'page', page: location.pathname, vw: window.innerWidth, vh: window.innerHeight });
    const timers = [300, 1500].map((delay) => window.setTimeout(() => hooks.current?.scan(), delay));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [location.pathname]);

  return null;
}
